import { env } from '@/shared/config/env';

/**
 * Keycloak client id this SPA authenticates through (via the merchant-portal BFF) — uniform across
 * every contour (see `.env.example`), so it is safe to reference as a literal here rather than another
 * env var. The Account Console reads it back via `referrer` to render a native "Back to <app>" link.
 */
const ACCOUNT_CONSOLE_REFERRER_CLIENT_ID = 'web-merchant-portal-bff';

/**
 * Builds a Keycloak Account Console URL: the configured console base URL
 * (`env.accountConsoleUrl`/`VITE_ACCOUNT_CONSOLE_URL`, per contour) plus the `referrer`/`referrer_uri`
 * query params the console uses to render a native "Back to Merchant Portal" link, optionally followed
 * by a `#…` deep-link fragment into a specific console section.
 *
 * Originally `buildAccountConsoleUrl` in `AppLayout.tsx` (the user menu's "Profile settings" link);
 * moved here (plan-platform-001 step 3.1) so `/account/security` (user-story-merchant-portal-060) can
 * reuse it — for the "Manage in account settings" CTA and the password/TOTP/sessions links card — rather
 * than the app growing a second way to build the same URL. `UserMenu` now calls this too.
 *
 * @param fragment optional deep link into a console section, e.g. `'#/security/signing-in'` — appended
 *   verbatim, after the query string (a URL fragment always comes last). Omitted entirely when absent,
 *   matching the user menu's original "just the console root" link.
 * @param returnPath SPA path the console's "Back to Merchant Portal" link should return to; combined
 *   with `window.location.origin` to build `referrer_uri`. Defaults to `/` (the original user-menu
 *   behaviour, before this page existed).
 */
export function accountConsoleUrl(fragment?: string, returnPath = '/'): string {
  const baseUrl = env.accountConsoleUrl;
  const separator = baseUrl.includes('?') ? '&' : '?';
  const referrerUri = encodeURIComponent(`${window.location.origin}${returnPath}`);
  const withQuery = `${baseUrl}${separator}referrer=${ACCOUNT_CONSOLE_REFERRER_CLIENT_ID}&referrer_uri=${referrerUri}`;
  return fragment ? `${withQuery}${fragment}` : withQuery;
}
